import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Stack,
  Alert,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useTheme, alpha } from "@mui/material/styles";
import {
  getPrescriptionEval,
  selectMovementEvals,
  selectPrescriptionEval,
  selectPrescriptionEvalLoading,
  selectPrescriptionEvalError,
} from "../../redux/slices/exerciseSlice";
import { SOURCE_META } from "../Exercise/PrescriptionEvalCard";
import {
  accentForIndex,
  adminSurfaceSx,
  adminTableWrapSx,
  adminTableHeadCellSx,
  adminTableRowSx,
  adminPageSx,
  adminToggleGroupSx,
  adminChipSx,
  MONO,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import AdminSectionHeader from "./AdminSectionHeader";
import { adminRequest } from "../../utils/adminApi";

// doc 29, Phase 9 — P9.9. Admin-only eval surface: per movement, the
// prescription SOURCE breakdown (set_logs | scalar_history | default |
// progression) and the headline OVERRIDE RATE (§F) — the share of confirmed
// prescribed sets whose final actual_* deviated from target_*. Mirrors the
// EventsDashboard pattern (axios proxy + Bearer token + admin styles).
//
// Consumes the worker route GET /api/admin/prescriptions (proxied via
// functions/api/admin/getPrescriptions). That route is LIVE — shipped in the
// worker PR #140-era lane, now on main and deployed to prod, returning the
// per-movement { sources, override_rate } aggregate. Post-loading the page
// distinguishes three states: a real error (rejected request), a success-but-
// empty window (no prescribed-set events yet), and data (cards + table).

const WINDOW_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const SOURCE_KEYS = ["progression", "set_logs", "scalar_history", "default"];

const sourceLabel = (source) => SOURCE_META[source]?.label ?? source;

const formatPct = (rate) => `${Math.round(rate * 100)}%`;

export default function PrescriptionsDashboard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token + routes via envParams ({ env: 'staging' }) (adminRequest).
  const { env, token, envParams } = useSelector(adminRequest);
  const movements = useSelector(selectMovementEvals);
  const raw = useSelector(selectPrescriptionEval);
  const loading = useSelector(selectPrescriptionEvalLoading);
  const error = useSelector(selectPrescriptionEvalError);
  const [days, setDays] = useState(30);

  useEffect(() => {
    // Same gate as the other dashboards: skip in staging until a token is held.
    if (token) {
      dispatch(getPrescriptionEval({ token, days, envParams }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, env, token, days]);

  // Cohort-wide source breakdown across all movements, for the top stat cards.
  const cohortSources = useMemo(() => {
    const totals = {};
    let grand = 0;
    movements.forEach((m) => {
      m.sourceBreakdown.forEach(({ source, count }) => {
        totals[source] = (totals[source] ?? 0) + count;
        grand += count;
      });
    });
    return SOURCE_KEYS.filter((k) => totals[k] != null)
      .concat(Object.keys(totals).filter((k) => !SOURCE_KEYS.includes(k)))
      .map((source) => ({
        source,
        count: totals[source] ?? 0,
        pct: grand > 0 ? (totals[source] ?? 0) / grand : 0,
      }));
  }, [movements]);

  // Cohort override rate (weighted by # of confirmed prescribed sets).
  const cohortOverride = useMemo(() => {
    let overrides = 0;
    let total = 0;
    let any = false;
    movements.forEach((m) => {
      if (m.override?.status === "ready") {
        any = true;
        overrides += m.override.overrides ?? 0;
        total += m.override.total ?? 0;
      }
    });
    if (!any || total === 0) return { status: "pending" };
    return { status: "ready", rate: overrides / total, overrides, total };
  }, [movements]);

  // Post-loading, three genuinely distinct states (the route is live now):
  //   1. error   — the request rejected (auth/network/worker error).
  //   2. empty    — fulfilled OK but no movements in the window (no prescribed-
  //                 set events yet). NOT an error.
  //   3. data     — movements present → cards + table.
  const showError = !loading && !!error;
  const showEmpty = !loading && !error && movements.length === 0;
  const windowLabel = raw?.window_days ?? days;

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<TuneIcon />}
          title="Prescriptions &amp; override rate"
          subtitle={
            <>
              Progressive-overload eval — source mix &amp; override rate per
              movement (doc 29 §F)
              {raw?.window_days ? `, last ${raw.window_days}d` : ""}
            </>
          }
          actions={
            <ToggleButtonGroup
              value={days}
              exclusive
              size="small"
              onChange={(_e, v) => v && setDays(v)}
              sx={adminToggleGroupSx(theme)}
            >
              {WINDOW_OPTIONS.map((opt) => (
                <ToggleButton key={opt.days} value={opt.days}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : showError ? (
          <Alert
            severity="error"
            variant="outlined"
            data-testid="prescriptions-error"
            sx={{
              "& .MuiAlert-message": { color: theme.palette.text.primary },
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Couldn&apos;t load the prescription eval
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              The request to <code>GET /api/admin/prescriptions</code> failed.
              Check that you&apos;re authenticated as an admin, then switch the
              window above to retry.
            </Typography>
            {error?.error ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  fontFamily: MONO,
                  color: theme.palette.text.secondary,
                }}
              >
                worker said: {String(error.error)}
              </Typography>
            ) : null}
          </Alert>
        ) : showEmpty ? (
          <Alert
            severity="info"
            variant="outlined"
            data-testid="prescriptions-empty"
            sx={{
              "& .MuiAlert-message": { color: theme.palette.text.primary },
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              No prescription events in the last {windowLabel}d
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              This eval populates as users log prescribed sets — nothing has
              landed in this window yet. Try a wider window above.
            </Typography>
          </Alert>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={adminSurfaceSx(theme)}>
                  <CardContent>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: theme.palette.primary.light }}
                    >
                      {cohortOverride.status === "ready"
                        ? formatPct(cohortOverride.rate)
                        : "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Cohort override rate
                      {cohortOverride.status === "ready"
                        ? ` (${cohortOverride.overrides}/${cohortOverride.total} sets)`
                        : " (pending per-set actuals)"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {cohortSources.map((s, i) => {
                const accent = accentForIndex(i);
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.source}>
                    <Card sx={adminSurfaceSx(theme)}>
                      <CardContent>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: accent }}
                        >
                          {formatPct(s.pct)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            wordBreak: "break-word",
                          }}
                        >
                          {sourceLabel(s.source)} ({s.count.toLocaleString()})
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <AdminSectionHeader
                  label="Per movement"
                  accent={theme.palette.primary.main}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ ...adminTableWrapSx(theme), overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Movement
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)} align="right">
                          Prescribed sets
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Source mix
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)} align="right">
                          Override rate
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movements.map((m) => (
                        <TableRow key={m.exercise_name} sx={adminTableRowSx}>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {m.exercise_name}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {(m.total ?? 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {m.sourceBreakdown.map((s) => (
                                <Chip
                                  key={s.source}
                                  size="small"
                                  label={`${sourceLabel(s.source)} ${formatPct(
                                    s.pct
                                  )}`}
                                  sx={adminChipSx(theme)}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {m.override?.status === "ready" ? (
                              <Box sx={{ minWidth: 90, ml: "auto" }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {formatPct(m.override.rate)}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, m.override.rate * 100)}
                                  sx={{
                                    mt: 0.5,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: alpha("#ffffff", 0.08),
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  {m.override.overrides}/{m.override.total}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip
                                size="small"
                                color="warning"
                                label="Data pending"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}
